<template>
  <div class="notification-container">
    <MetaManager
        title="Send Notifications"
        description="Send notifications via text or site notifcations to all your followers"
      />
    <!-- Token Count Header -->
    <div class="token-header" v-if="!isAdminOnlySending">
      <p>Tokens Left:</p>
      <h2>{{ tokensLeft }}</h2>
<!--       <button class="get-more-button" @click="getMoreTokens">Get More</button> -->
    </div>
    
    <!-- Form Content -->
    <div class="content-container">
      <!-- Choose Sender Type for users who are both admin and business -->
      <div class="form-group" v-if="isAdmin && isBusiness">
        <label for="senderType">Send As</label>
        <select v-model="senderType" id="senderType" @change="handleSenderTypeChange">
          <option value="business">Business Owner</option>
          <option value="admin">Site Admin</option>
        </select>
      </div>

      <!-- Admin audience selection -->
      <div class="form-group" v-if="isAdminOnlySending">
        <label for="adminAudience">Audience</label>
        <select v-model="adminAudienceType" id="adminAudience">
          <option value="all">All Users</option>
          <option value="giftcard_buyers">Gift card buyers of a business</option>
        </select>
      </div>

      <!-- Target Business selector (admin targeting buyers) -->
      <div class="form-group" v-if="isAdminOnlySending && adminAudienceType === 'giftcard_buyers'">
        <label for="targetBusiness">Business</label>

        <!-- Custom searchable dropdown -->
        <div class="searchable-dropdown" @click="toggleDropdown" @focus="ensureBusinessesLoaded">
          <div class="dropdown-display" :class="{ 'dropdown-open': dropdownOpen }">
            <input
              type="text"
              v-model="businessSearchDisplay"
              :placeholder="selectedBusinessName || 'Select a business'"
              readonly
              class="dropdown-input"
            />
            <span class="dropdown-arrow">▼</span>
          </div>
          
          <div v-if="dropdownOpen" class="dropdown-menu">
            <input
              type="text"
              v-model="businessSearch"
              placeholder="Search businesses..."
              class="dropdown-search"
              @click.stop
              ref="searchInput"
            />
            <div class="dropdown-options">
              <div
                v-for="b in filteredBusinesses"
                :key="b._id"
                class="dropdown-option"
                @click="selectBusiness(b)"
              >
                {{ b.business_name }}
              </div>
              <div v-if="businessesLoaded && businesses.length === 0" class="dropdown-empty">
                No businesses found.
              </div>
              <div
                v-else-if="businessesLoaded && businesses.length > 0 && filteredBusinesses.length === 0"
                class="dropdown-empty"
              >
                No matching businesses.
              </div>
            </div>
          </div>
        </div>

        <!-- Email count readout -->
        <small v-if="targetBusinessId" style="margin-top:6px;color:#555;">
          Will email: {{ giftBuyerEmailCount }} {{ giftBuyerEmailCount === 1 ? 'address' : 'addresses' }}
        </small>
      </div>

      <!-- Choose Notification Type -->
      <div class="form-group">
        <label for="notificationType">Choose a notification type</label>
        <select v-model="notificationType" id="notificationType" @change="calculateTokenCost" :disabled="isAdminOnlySending">
          <option value="notification">Site Notification</option>
          <option value="text" v-if="!isAdminOnlySending">Text</option> 
        </select>
      </div>

      <!-- From (Business Name) -->
      <div class="form-group">
        <label for="from">From</label>
        <input type="text" v-model="from" id="from" readonly>
      </div>

      <!-- Title -->
      <div class="form-group" v-if="notificationType !== 'text'">
        <label for="title">Title</label>
        <input type="text" v-model="title" id="title">
      </div>

      <!-- Message Body (Quill Editor) -->
      <div class="form-group">
        <label for="messageBody">Message</label>
        <div id="messageEditor"></div>
      </div>

      <!-- Token Cost -->
      <div class="form-group" v-if="!isAdminOnlySending">
        <label for="tokenCost">Token cost</label>
        <input type="number" v-model="tokenCost" id="tokenCost" readonly>
      </div>

      <!-- Buttons -->
      <div class="button-group">
        <button
          class="send-button"
          @click="sendNotification"
          :disabled="
            isSending ||
            notificationSent ||
            (isAdminOnlySending && adminAudienceType === 'giftcard_buyers' && !targetBusinessId)
          "
        >Send</button>
        <button class="cancel-button" @click="cancelNotification">Cancel</button>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="isSending || notificationSent" class="loading-overlay">
      <div v-if="isSending" class="loader"></div>
      <p v-if="isSending">Sending notification... Do not refresh or close this page.</p>
      <p v-if="notificationSent" class="success-message">Notification sent successfully!</p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, computed, nextTick } from 'vue';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import axios from 'axios';
import MetaManager from '../../components/MetaManager.vue';

export default {
  components: {
    MetaManager,
  },
  setup() {
    const tokensLeft = ref(0);
    const numberOfFans = ref(0);
    const totalUsers = ref(0);
    const notificationType = ref('notification');
    const from = ref('');
    const title = ref('');
    const tokenCost = ref(0);
    const isSending = ref(false);
    const notificationSent = ref(false);
    const maxCharacters = ref(4000);
    const characterCount = ref(0);
    const isAdmin = ref(false);
    const isBusiness = ref(false);
    const senderType = ref('business');
    let quill;

    // Admin targeting state
    const adminAudienceType = ref('all'); // 'all' | 'giftcard_buyers'
    const targetBusinessId = ref('');
    const businesses = ref([]);
    const giftBuyerEmailCount = ref(0);
    const businessesLoaded = ref(false);

    // Custom dropdown state
    const dropdownOpen = ref(false);
    const businessSearch = ref('');
    const businessSearchDisplay = ref('');
    const searchInput = ref(null);

    // Computed properties
    const filteredBusinesses = computed(() => {
      const q = businessSearch.value.trim().toLowerCase();
      if (!q) return businesses.value;
      return businesses.value.filter(b => {
        const name = (b.business_name || '').toLowerCase();
        const id = String(b._id || '').toLowerCase();
        return name.includes(q) || id.includes(q);
      });
    });

    const selectedBusinessName = computed(() => {
      const selected = businesses.value.find(b => b._id === targetBusinessId.value);
      return selected ? selected.business_name : '';
    });

    // Computed property to check if sending as admin only
    const isAdminOnlySending = computed(() => {
      return isAdmin.value && senderType.value === 'admin';
    });

    // Handle change in sender type
    const handleSenderTypeChange = () => {
      if (senderType.value === 'admin') {
        from.value = 'StarkShopper';
        notificationType.value = 'notification'; // Force site notification for admin
      } else {
        fetchBusinessName();
      }
      calculateTokenCost();
    };

    const toolbarOptionsConfig = {
      text: [
        ['bold', 'italic', 'underline'], // basic text formatting
      ],
      notification: [
        ['bold', 'italic', 'underline'], // basic text formatting
        [{ 'list': 'ordered' }, { 'list': 'bullet' }], // lists
        [{ 'align': [] }], // text alignment
        ['image'], // allow images
      ],
    };

    const formatsConfig = {
      text: ['bold', 'italic', 'underline'],
      notification: ['bold', 'italic', 'underline', 'list', 'align', 'image'],
    };

    const initializeEditor = () => {
      const editorElement = document.getElementById('messageEditor');

      // Clear existing Quill instance
      if (quill) {
        quill.off('text-change'); // Remove listeners
        quill.root.innerHTML = ''; // Clear content
        const toolbars = document.querySelectorAll('.ql-toolbar');
        toolbars.forEach(toolbar => toolbar.remove()); // Remove any lingering toolbars
        quill = null; // Nullify Quill instance
      }

      // Reinitialize Quill with new configuration
      quill = new Quill(editorElement, {
        theme: 'snow',
        modules: {
          toolbar: toolbarOptionsConfig[notificationType.value],
        },
        formats: formatsConfig[notificationType.value],
      });

      // Character limit logic
      quill.on('text-change', () => {
        characterCount.value = quill.getText().trim().length;
        if (characterCount.value > maxCharacters.value) {
          quill.deleteText(maxCharacters.value, characterCount.value);
        }
      });
    };

    watch(notificationType, (newType) => {
      if (newType === 'text') {
        title.value = ''; // Clear title for text messages
      }
      initializeEditor();
      calculateTokenCost();
    });

    onMounted(async () => {
      try {
        // Check if user is admin
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        isAdmin.value = user.isAdmin === true;
        isBusiness.value = !!user.business_id;
        
        // Set default sender type
        if (isAdmin.value && !isBusiness.value) {
          senderType.value = 'admin';
          from.value = 'StarkShopper';
        } else if (isAdmin.value && isBusiness.value) {
          // Default to business if user has both roles
          senderType.value = 'business'; 
          await fetchBusinessName();
        } else {
          senderType.value = 'business';
          await fetchBusinessName();
        }

        // Fetch tokens if needed - but don't await this to prevent blocking
        if (!isAdminOnlySending.value) {
          fetchTokensLeft().catch(err => console.warn('Error fetching tokens:', err));
        }

        // Initialize the editor immediately
        initializeEditor();
        
        // Fetch total users after initialization if needed - don't await
        if (isAdmin.value) {
          fetchTotalUsers();
        }

        // Fetch businesses for admin targeting
        if (isAdmin.value) {
          fetchBusinesses().catch(() => {});
        }
        // If admin and already picked buyers + business, prefetch count
        if (isAdmin.value && adminAudienceType.value === 'giftcard_buyers' && targetBusinessId.value) {
          fetchGiftBuyerEmailCount(targetBusinessId.value).catch(() => {});
        }
      } catch (error) {
        console.error('Error in component initialization:', error);
        // Initialize the editor even if other operations fail
        initializeEditor();
      }
    });

    // Fetch businesses for admin audience targeting
    const fetchBusinesses = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/api/notifications/businesses`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        businesses.value = Array.isArray(data?.businesses) ? data.businesses : [];
      } catch (e) {
        console.warn('Error fetching businesses for admin targeting:', e);
        businesses.value = [];
      } finally {
        businessesLoaded.value = true;
      }
    };

    // Ensure list is loaded when user focuses the dropdown
    const ensureBusinessesLoaded = () => {
      if (!businessesLoaded.value) {
        fetchBusinesses();
      }
    };

    // Fetch the business name
    const fetchBusinessName = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user.business_id) {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/businesses/business-name/${user.business_id}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          from.value = response.data.businessName;
        }
      } catch (error) {
        console.error('Error fetching business name:', error);
        from.value = 'Unknown Business';
      }
    };

    // Fetch the number of tokens left for the business
    const fetchTokensLeft = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user.business_id) {
          const token = localStorage.getItem('token');
          const response = await axios.post(`${process.env.VUE_APP_API_URL}/api/notifications/tokens-left`, {
            businessId: user.business_id
          }, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          tokensLeft.value = response.data.tokensLeft;
        }
      } catch (error) {
        console.error('Error fetching tokens left:', error);
      }
    };

    // Fetch the number of fans for the business
    const fetchNumberOfFans = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user.business_id) {
          const token = localStorage.getItem('token');
          const response = await axios.post(`${process.env.VUE_APP_API_URL}/api/notifications/notification-fans`, {
            businessId: user.business_id
          }, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          numberOfFans.value = response.data.numberOfSubscribers;
        }
      } catch (error) {
        console.error('Error fetching number of fans:', error);
      }
    };

    // Fetch total number of users (for admin notifications) - simpler approach
    const fetchTotalUsers = async () => {
      try {
        // Skip any authentication headers - just make a simple GET request
        axios.get(`${process.env.VUE_APP_API_URL}/api/businesses/total-users`)
          .then(response => {
            totalUsers.value = response.data.count;
          })
          .catch(() => {
            // If there's an error, just use a default value
            totalUsers.value = 1000;
          });
      } catch (error) {
        console.warn('Error in fetchTotalUsers function:', error);
        totalUsers.value = 1000;
      }
    };

    // Calculate the token cost with better error handling
    const calculateTokenCost = async () => {
      // If sending as admin, no tokens needed
      if (isAdminOnlySending.value) {
        tokenCost.value = 0;
        return;
      }

      try {
        await fetchNumberOfFans(); // Fetch the number of fans whenever the notification type changes
      } catch (error) {
        console.warn('Error fetching fans count:', error);
        // Continue with default values if fetching fails
      }

      if (notificationType.value === 'notification') {
        tokenCost.value = 0;
      } else if (notificationType.value === 'email') {
        tokenCost.value = Math.ceil(numberOfFans.value);
      } else if (notificationType.value === 'text') {
        tokenCost.value = Math.ceil(numberOfFans.value);
      }
    };

    // Watch for changes to notificationType and recalculate the token cost
    watch(notificationType, () => {
      calculateTokenCost();
    });

    // Watch for changes in sender type
    watch(senderType, () => {
      calculateTokenCost();
    });

    // Watch business selection to update email count
    watch(targetBusinessId, (val) => {
      if (isAdminOnlySending.value && adminAudienceType.value === 'giftcard_buyers') {
        fetchGiftBuyerEmailCount(val);
      }
    });

    // Also fetch businesses/count when switching to buyers audience
    watch(adminAudienceType, (val) => {
      if (isAdminOnlySending.value && val === 'giftcard_buyers') {
        fetchBusinesses();
        if (targetBusinessId.value) fetchGiftBuyerEmailCount(targetBusinessId.value);
      } else {
        giftBuyerEmailCount.value = 0;
      }
    });

    // If switching to Admin sender, make sure list loads
    watch(isAdminOnlySending, (val) => {
      if (val && adminAudienceType.value === 'giftcard_buyers') {
        fetchBusinesses();
      }
    });

    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const sendNotification = async () => {
      isSending.value = true;

      // Extract HTML content from the Quill editor
      let messageContent = quill.root.innerHTML;

      // Find all images in the editor content
      const imageTags = Array.from(quill.root.querySelectorAll('img'));
      const uploadedImages = {};

      for (const imgTag of imageTags) {
        const imgSrc = imgTag.getAttribute('src');
        if (imgSrc && imgSrc.startsWith('data:')) {
          // It's a base64 image, upload it to the server
          const formData = new FormData();
          formData.append('image', dataURItoBlob(imgSrc));

          try {
            const response = await axios.post(`${process.env.VUE_APP_API_URL}/api/notifications/upload`, formData, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'multipart/form-data',
              },
            });

            // Replace the image src in the content with the uploaded image URL
            const uploadedUrl = response.data.url;
            uploadedImages[imgSrc] = uploadedUrl;
          } catch (error) {
            console.error('Error uploading image:', error);
            return; // Stop if image upload fails
          }
        }
      }

      // Replace all image src attributes in the Quill content
      for (const [originalSrc, uploadedUrl] of Object.entries(uploadedImages)) {
        messageContent = messageContent.replace(originalSrc, uploadedUrl);
      }

      // Send the final content to the server
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const token = localStorage.getItem('token');
      const isAdminSending = isAdmin.value && senderType.value === 'admin';
      const businessId = isAdminSending ? null : user.business_id;

      try {
        const payload = {
          businessId,
          title: notificationType.value === 'text' ? '' : title.value,
          messageBody: messageContent,
          tokensCost: tokenCost.value,
          notificationType: notificationType.value,
          isAdminNotification: isAdminSending
        };

        // Include admin targeting params
        if (isAdminSending && adminAudienceType.value === 'giftcard_buyers') {
          payload.adminTargetType = 'giftcard_buyers';
          payload.targetBusinessId = targetBusinessId.value;
        }

        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/notifications/new-notification`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log('Notification sent successfully:', response.data);
        notificationSent.value = true;

        await delay(2000);
      } catch (error) {
        console.error('Error sending notification:', error);
      } finally {
        isSending.value = false;
        notificationSent.value = false;
        location.reload();
      }
    };

    const cancelNotification = () => {
      location.reload();
    };

    // Fetch distinct email count for gift card buyers of a business
    const fetchGiftBuyerEmailCount = async (bizId) => {
      try {
        if (!bizId) {
          giftBuyerEmailCount.value = 0;
          return;
        }
        const token = localStorage.getItem('token');
        const { data } = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/notifications/giftcard-buyer-count`,
          {
            params: { businessId: bizId },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        giftBuyerEmailCount.value = Number(data?.count || 0);
      } catch (e) {
        console.warn('Error fetching gift card buyer email count:', e);
        giftBuyerEmailCount.value = 0;
      }
    };

    // Helper function to convert base64 image to Blob
    const dataURItoBlob = (dataURI) => {
      const byteString = atob(dataURI.split(',')[1]);
      const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      return new Blob([ab], { type: mimeString });
    };

    // Custom dropdown methods
    const toggleDropdown = () => {
      dropdownOpen.value = !dropdownOpen.value;
      if (dropdownOpen.value) {
        ensureBusinessesLoaded();
        nextTick(() => {
          if (searchInput.value) {
            searchInput.value.focus();
          }
        });
      }
    };

    const selectBusiness = (business) => {
      targetBusinessId.value = business._id;
      dropdownOpen.value = false;
      businessSearch.value = '';
    };

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest('.searchable-dropdown')) {
        dropdownOpen.value = false;
        businessSearch.value = '';
      }
    };

    onMounted(async () => {
      // Add click outside listener
      document.addEventListener('click', handleClickOutside);
    });

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      tokensLeft,
      notificationType,
      from,
      title,
      tokenCost,
      sendNotification,
      cancelNotification,
      isSending,
      notificationSent,
      maxCharacters,
      characterCount,
      isAdmin,
      isBusiness,
      senderType,
      isAdminOnlySending,
      handleSenderTypeChange,
      // admin targeting bindings
      adminAudienceType,
      targetBusinessId,
      businesses,
      giftBuyerEmailCount,
      businessesLoaded,
      ensureBusinessesLoaded,
      // Custom dropdown bindings
      dropdownOpen,
      businessSearch,
      businessSearchDisplay,
      searchInput,
      filteredBusinesses,
      selectedBusinessName,
      toggleDropdown,
      selectBusiness
    };
  }
};
</script>

<style scoped>
.notification-container {
  width: 100%;
  max-width: 600px;
  margin: 20px auto;
  padding: 20px;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  position: relative;
  overflow: visible;
}

.token-header {
  text-align: center;
  margin-bottom: 20px;
  padding: 10px;
  background-color: #f8f8f8;
  border-radius: 8px;
}

.token-header p {
  font-size: 16px;
}

.token-header h2 {
  font-size: 32px;
  color: #333;
  margin: 5px 0;
}

.get-more-button {
  background-color: #4C6B30;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
}

.content-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
  position: relative;
  overflow: visible;
}

.form-group {
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: visible;
}

label {
  margin-bottom: 6px;
  font-weight: bold;
}

select,
input {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}

input[readonly] {
  background-color: #f8f8f8;
}

#messageEditor {
  height: 150px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.button-group {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.send-button,
.cancel-button {
  width: 48%;
  padding: 10px;
  background-color: #4C6B30;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
}

.cancel-button {
  background-color: #E0E0E0;
  color: black;
}

.send-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

input:focus,
select:focus,
textarea:focus {
  border-color: #6E9F49;
  outline: none;
  box-shadow: 0 0 5px rgba(110, 159, 73, 0.5);
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loader {
  border: 5px solid #f3f3f3;
  border-top: 5px solid #4C6B30;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.success-message {
  color: #4C6B30;
  font-weight: bold;
  font-size: 18px;
}

@media(max-width: 1024px){
  .notification-container{
    margin-top: 100px;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .notification-container {
    padding: 15px;
  }

  .button-group {
    flex-direction: column;
    gap: 10px;
  }

  .send-button,
  .cancel-button {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .notification-container {
    width: 90%;
  }
}

/* Custom searchable dropdown styles */
.searchable-dropdown {
  position: relative;
  width: 100%;
  z-index: 10;
}

.dropdown-display {
  position: relative;
  width: 100%;
  cursor: pointer;
}

.dropdown-input {
  width: 100%;
  padding: 10px 30px 10px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
  cursor: pointer;
  box-sizing: border-box;
}

.dropdown-input:focus {
  border-color: #6E9F49;
  outline: none;
  box-shadow: 0 0 5px rgba(110, 159, 73, 0.5);
}

.dropdown-open .dropdown-input {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom-color: #6E9F49;
}

.dropdown-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #666;
  font-size: 12px;
  transition: transform 0.2s;
}

.dropdown-open .dropdown-arrow {
  transform: translateY(-50%) rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #6E9F49;
  border-top: none;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  max-height: 280px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
}

.dropdown-search {
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-bottom: 1px solid #eee;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
}

.dropdown-search:focus {
  background-color: #f8f9fa;
}

.dropdown-options {
  max-height: 220px;
  overflow-y: auto;
}

.dropdown-option {
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
  word-wrap: break-word;
}

.dropdown-option:hover {
  background-color: #f8f9fa;
}

.dropdown-option:last-child {
  border-bottom: none;
}

.dropdown-empty {
  padding: 10px;
  color: #888;
  font-style: italic;
  text-align: center;
}
</style>
